import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { alignmentWalkAtom } from '@/atoms/alignment.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import {
  axisTicks,
  barHeightPx,
  barKind,
  CLEARANCE_FILL,
  CLEARANCE_LEGEND_KEYS,
  CLEARANCE_LEGEND_ORDER,
  groupClearanceBins,
  metricMax,
  metricValue,
  type ClearanceMetric,
  type ClearanceSuperBin,
} from '@/lib/clearance/chart';
import type { ClearanceJson } from '@/lib/clearance/types';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import { barWidthFor, CHART_MARGIN, LABEL_MIN_GAP_PX, maxBarsFor, thinLabels } from '@/lib/stationVolumes/chartLayout';
import { formatFixed, staOnly, staTag } from '@/lib/stationVolumes/format';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';

export interface ClearanceChartProps {
  json: ClearanceJson;
  layerId: string;
  width: number;
  height: number;
  /** Owned by the dock so the choice survives a trip to the Table tab (L6). */
  metric: ClearanceMetric;
  onMetricChange: (next: ClearanceMetric) => void;
}

export type { ClearanceMetric };

/**
 * Ticks are a legibility aid, not a quoted figure — 2 dp is plenty for a
 * depth, and a count axis that runs to the hundreds of thousands is read
 * faster as "660K" than as a seven-digit run. Compact only above 10 000, so a
 * small run keeps exact tick values.
 */
const tickText = (v: number, metric: ClearanceMetric): string => {
  if (metric !== 'count') return formatFixed(v, 2);
  const n = Math.round(v);
  return n >= 10_000
    ? n.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 })
    : n.toLocaleString();
};

/**
 * Violations per bin, as a bar chart with one toggle: count, or max intrusion
 * depth. Two metrics rather than two panes — a clearance run has no cumulative
 * quantity, so the station dock's second (mass-haul) pane has no analogue here
 * and inventing one would be decoration.
 *
 * Reuses the station dock's layout constants and its display-station
 * formatter, so the two charts' axes read identically; it does NOT reuse
 * `BarPane`, which is typed on cut/fill volumes.
 *
 * Keyboard: the plot is ONE tab stop, not one per bar — a corridor-length run
 * has thousands of bars and a tab stop each would make the dock untraversable.
 * Arrow keys move the cursor, Enter/Space brushes it, Escape clears.
 */
export const ClearanceChart = ({ json, layerId, width, height, metric, onMetricChange }: ClearanceChartProps) => {
  const { t } = useTranslation('project');
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);
  const [cursor, setCursor] = useState(0);

  const innerW = Math.max(80, width - CHART_MARGIN.left - CHART_MARGIN.right);
  const innerH = Math.max(40, height - CHART_MARGIN.top - CHART_MARGIN.bottom);

  // Aggregate before drawing: one <rect> plus two pointer handlers per source
  // bin is fine at 250 bins and ruinous at 4 000 (M3).
  const { bins, groupSize } = useMemo(() => groupClearanceBins(json.bins, maxBarsFor(innerW)), [json.bins, innerW]);
  const barW = barWidthFor(innerW, Math.max(bins.length, 1));
  const maxValue = useMemo(() => metricMax(bins, metric), [bins, metric]);
  const ticks = useMemo(() => axisTicks(maxValue, 4, metric === 'count'), [maxValue, metric]);
  // Read off the SOURCE bins, not the grouped ones: a super-bin that swallows
  // one unscored bin among ten measured ones still has points, and the reader
  // is entitled to know that some ground under this axis was never swept.
  const anyUnscored = useMemo(() => json.bins.some((b) => b.pointCount === 0), [json.bins]);
  const palette = useSvPalette();

  const sel = selection && selection.layerId === layerId ? selection : null;
  const bandCentre = (i: number) => (innerW * (i + 0.5)) / Math.max(bins.length, 1);
  const labelEvery = thinLabels(
    bins.map((_, i) => CHART_MARGIN.left + bandCentre(i)),
    LABEL_MIN_GAP_PX,
  );

  // The bar the readout describes: what the pointer is over, else the keyboard
  // cursor's bar once the plot has focus, else the first brushed bar. Without
  // this the hover atom is written and never read back as a figure — the Chart
  // tab could not show a single number (M1).
  const hovered = hoverBin === null ? null : (bins.find((b) => hoverBin >= b.fromBin && hoverBin <= b.toBin) ?? null);
  const brushed = sel ? (bins.find((b) => b.toBin >= sel.fromBin && b.fromBin <= sel.toBin) ?? null) : null;
  const readoutBin: ClearanceSuperBin | null = hovered ?? brushed;

  /**
   * Brushing a bar also MOVES the walk to it — the station-volumes chart's
   * exact contract (`StationVolumeChart`'s own click handler): the cursor
   * goes to the bar's centre, `follow` is never switched on. A click is a
   * request to look at a station, not a request to have the camera taken
   * over; the walk toolbar's ⦿ is where the user asks for that.
   *
   * The write is a no-op unless a walk is already running on THIS run's
   * alignment, so a bar click can neither start a walk behind the gate nor
   * yank a walk that is following a different alignment.
   *
   * Keyboard comes along for free: `onKeyDown`'s Enter/Space calls this.
   */
  const brush = (b: ClearanceSuperBin) => {
    setSelection({ layerId, fromBin: b.fromBin, toBin: b.toBin });
    const station = binCentreStation(b);
    setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
  };

  const onKeyDown = (e: ReactKeyboardEvent<SVGSVGElement>) => {
    if (bins.length === 0) return;
    const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (step !== 0) {
      e.preventDefault();
      const next = Math.max(0, Math.min(bins.length - 1, cursor + step));
      setCursor(next);
      setHover(bins[next].fromBin);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      brush(bins[Math.max(0, Math.min(bins.length - 1, cursor))]);
      return;
    }
    if (e.key === 'Escape' && sel) {
      e.preventDefault();
      setSelection(null);
    }
  };

  const barLabel = (b: ClearanceSuperBin) =>
    t('clearance.chart.barLabel', {
      range: `${staOnly(b.staStartDisplayM, 0)} – ${staOnly(b.staEndDisplayM, 0)}`,
      violations: b.violationCount,
      depth: b.maxIntrusionM === null ? '—' : formatFixed(b.maxIntrusionM, 3),
      near: b.nearMiss.within0_1M,
    });

  return (
    <div className='flex min-w-0 flex-col gap-1'>
      <div className='flex items-center justify-between gap-3 text-xs'>
        <span data-testid='clearance-chart-metric'>
          {t(metric === 'count' ? 'clearance.chart.metricCount' : 'clearance.chart.metricDepth')}
          {groupSize > 1 ? ` · ${t('clearance.chart.grouped', { size: groupSize })}` : ''}
        </span>
        {/* The figures the chart would otherwise only encode as shape (M1). */}
        <span
          data-testid='clearance-chart-readout'
          aria-live='polite'
          className='min-w-0 truncate text-slate-600 tabular-nums dark:text-stone-300'>
          {readoutBin ? barLabel(readoutBin) : t('clearance.chart.readoutHint')}
        </span>
        <span
          data-testid='clearance-chart-legend'
          className='flex shrink-0 items-center gap-2 text-[10px] text-slate-600 dark:text-stone-300'>
          <span className='sr-only'>{t('clearance.chart.legendTitle')}</span>
          {CLEARANCE_LEGEND_ORDER.map((kind) => (
            <span key={kind} className='flex items-center gap-1'>
              <span
                aria-hidden
                data-testid='clearance-chart-legend-swatch'
                className='inline-block size-2 rounded-[1px]'
                style={{ background: CLEARANCE_FILL[kind] }}
              />
              {t(CLEARANCE_LEGEND_KEYS[kind])}
            </span>
          ))}
          {/* The fourth state of the K2 ladder, and the only one the bars have
              no way to draw: a bin the sweep never scored is not "clear", it is
              unmeasured — an empty slot on the axis that reads exactly like a
              zero. It used to be keyed only by the dock header's second copy of
              this legend, beside the 3D toggles; there is one key now, and this
              is it (the envelope's bands are painted from the same
              `CLEARANCE_FILL` map, so it describes both).

              A FLAT `zero` swatch, not the 45-degree hatch it used to be. The
              hatch was a picture of the corridor plates' own low-coverage
              stripes; the plates are gone, the envelope paints an unscored span
              flat pale, and a key showing a pattern nothing on screen draws is
              worse than no key at all. The outline is what keeps a near-white
              swatch visible on a near-white panel. */}
          {anyUnscored && (
            <span className='flex items-center gap-1' data-testid='clearance-chart-legend-empty'>
              <span
                aria-hidden
                className='inline-block h-2 w-4 rounded-[2px] outline outline-slate-400/50 dark:outline-stone-500/50'
                style={{ background: palette.zero }}
              />
              {t('clearance.bin.noPoints')}
            </span>
          )}
        </span>
        <button
          type='button'
          data-testid='clearance-metric-toggle'
          // A two-state control has to say what it switches TO, and expose the
          // state it is in — `aria-pressed` on the "show depth" affordance.
          aria-pressed={metric === 'depth'}
          className='shrink-0 rounded border border-slate-200 px-1.5 py-0.5 hover:bg-slate-50 dark:border-stone-700 dark:hover:bg-stone-800'
          onClick={() => onMetricChange(metric === 'count' ? 'depth' : 'count')}>
          {t(metric === 'count' ? 'clearance.chart.showDepth' : 'clearance.chart.showCount')}
        </button>
      </div>
      <svg
        width={width}
        height={height}
        role='group'
        tabIndex={0}
        aria-label={t('clearance.chart.alt')}
        onKeyDown={onKeyDown}
        onBlur={() => setHover(null)}
        className='rounded focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none'>
        <g transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}>
          {ticks.map((v) => {
            const y = innerH - (v / maxValue) * innerH;
            return (
              <g key={`tick-${v}`}>
                <line x1={0} x2={innerW} y1={y} y2={y} stroke='currentColor' strokeOpacity={v === 0 ? 0.35 : 0.12} />
                <text x={-6} y={y + 3} fontSize={10} textAnchor='end' fill='currentColor' opacity={0.7}>
                  {tickText(v, metric)}
                </text>
              </g>
            );
          })}
          {bins.map((b, i) => {
            const h = barHeightPx(b, metric, maxValue, innerH);
            // Centred in its band, so a bar and the station label under it
            // describe the same place (M2).
            const x = bandCentre(i) - Math.max(1, barW) / 2;
            const inSel = !sel || (b.toBin >= sel.fromBin && b.fromBin <= sel.toBin);
            const isHover = hovered?.i === b.i;
            return (
              <rect
                key={b.i}
                data-testid={`clearance-bar-${b.fromBin}`}
                data-kind={barKind(b)}
                data-value={metricValue(b, metric)}
                x={x}
                y={innerH - h}
                width={Math.max(1, barW)}
                height={h}
                rx={2}
                fill={CLEARANCE_FILL[barKind(b)]}
                opacity={inSel ? (isHover ? 1 : 0.85) : 0.3}
                onPointerEnter={() => setHover(b.fromBin)}
                onPointerLeave={() => setHover(null)}
                onClick={() => brush(b)}>
                <title>{barLabel(b)}</title>
              </rect>
            );
          })}
          {/* Where the keyboard cursor sits. Drawn after the bars so a 1 px
              stub is still findable without a mouse. */}
          {bins[cursor] && (
            <rect
              data-testid='clearance-chart-cursor'
              x={bandCentre(cursor) - Math.max(1, barW) / 2 - 1}
              y={0}
              width={Math.max(1, barW) + 2}
              height={innerH}
              fill='none'
              stroke='currentColor'
              strokeOpacity={0.25}
              strokeDasharray='2 2'
            />
          )}
          {bins.map((b, i) =>
            labelEvery[i] ? (
              <text
                key={`lb-${b.i}`}
                x={bandCentre(i)}
                y={innerH + 14}
                fontSize={10}
                textAnchor='middle'
                fill='currentColor'
                opacity={0.7}>
                {staTag(b.staStartDisplayM, 0)}
              </text>
            ) : null,
          )}
        </g>
      </svg>
    </div>
  );
};
